import * as React from "react";
import { forwardRef, ReactNode, Ref } from "react";
import cx from "classnames";
import baseButtonStyles from "../../buttons/common/BaseButton.module.css";
import buttonLinkStyles from "../ButtonLink.module.css";
import { AnchorElementProps } from "@stenajs-webui/core";
import { CommonButtonProps } from "../../buttons/common/ButtonCommon";
import { getButtonLabel } from "../../buttons/common/ButtonLabelFactory";
import { ButtonContent } from "../../buttons/common/ButtonContent";

export interface BaseButtonLinkRenderProps extends AnchorElementProps {
  className: string;
  children: ReactNode;
  ref?: Ref<HTMLAnchorElement>;
}

export interface BaseButtonLinkProps
  extends CommonButtonProps,
    AnchorElementProps {
  /** Render the link as a custom element (e.g. a router link).
   * Spread the supplied props onto your element to inherit styling, ref, anchor
   * props and the pre-rendered button content (label, icons, loading state). */
  render?: (props: BaseButtonLinkRenderProps) => ReactNode;
}

export const BaseButtonLink = forwardRef<
  HTMLAnchorElement,
  BaseButtonLinkProps
>(function BaseButtonLink(
  {
    label,
    className,
    size = "medium",
    loadingLabel,
    loading = false,
    success = false,
    successLabel,
    leftIcon,
    left,
    rightIcon,
    right,
    responsiveIconOnly,
    render,
    ...anchorProps
  },
  ref,
) {
  const buttonLabel = getButtonLabel(
    label,
    success,
    successLabel,
    loading,
    loadingLabel,
  );

  const hasLabel = Boolean(
    (label && !success && !loading) ||
      (success && successLabel) ||
      (loading && loadingLabel),
  );

  const labelOnly = hasLabel && !left && !leftIcon && !right && !rightIcon;
  const iconOnly = leftIcon && !hasLabel && !left && !right && !rightIcon;

  const mergedClassName = cx(
    buttonLinkStyles.buttonLink,
    baseButtonStyles.button,
    baseButtonStyles[size],
    iconOnly && baseButtonStyles.iconOnly,
    labelOnly && baseButtonStyles.labelOnly,
    className,
  );

  const innerContent = (
    <ButtonContent
      success={success}
      loading={loading}
      leftIcon={leftIcon}
      left={left}
      right={right}
      rightIcon={rightIcon}
      label={buttonLabel}
      responsiveIconOnly={responsiveIconOnly}
    />
  );

  if (render) {
    return render({
      ...anchorProps,
      className: mergedClassName,
      ref,
      children: innerContent,
    });
  }

  return (
    <a ref={ref} className={mergedClassName} {...anchorProps}>
      {innerContent}
    </a>
  );
});
